import { useState } from 'react'
import { useTenantJobs, TenantJob, JobStep } from '@/api/devices'

interface Props {
  tenantId: string | null
}

type StatusFilter = 'all' | 'pending' | 'running' | 'failed' | 'success'

const STATUS_COLORS: Record<string, string> = {
  pending:  '#94a3b8',
  running:  '#60a5fa',
  success:  '#4ade80',
  failed:   '#f87171',
}

const STEP_COLORS: Record<string, string> = {
  running: '#60a5fa',
  success: '#4ade80',
  failed:  '#f87171',
}

function StatusBadge({ status }: { status: string }) {
  const color = STATUS_COLORS[status] ?? '#94a3b8'
  const bg = color + '1a'
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: bg, color, border: `1px solid ${color}33`,
      borderRadius: 'var(--radius-sm)', padding: '2px 9px', fontSize: 11, fontWeight: 600,
    }}>
      {status === 'running' ? '⟳ ' : ''}{status}
    </span>
  )
}

function StepsPanel({ job }: { job: TenantJob }) {
  return (
    <div style={{
      background: 'rgba(0,0,0,0.25)',
      borderRadius: 'var(--radius-sm)',
      padding: '12px 16px',
      marginTop: 8,
    }}>
      {/* Error banner */}
      {job.error && (
        <div style={{
          background: 'rgba(220,38,38,0.1)',
          border: '1px solid rgba(220,38,38,0.3)',
          borderRadius: 'var(--radius-sm)',
          padding: '10px 14px',
          marginBottom: 12,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#f87171', textTransform: 'uppercase', marginBottom: 4 }}>
            Errore
          </div>
          <pre style={{
            margin: 0, fontFamily: 'var(--font-mono)', fontSize: 12,
            color: '#fca5a5', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          }}>
            {job.error}
          </pre>
        </div>
      )}

      {/* Steps */}
      {job.steps.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Nessuno step registrato.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              {['Step', 'Status', 'Dettaglio', 'Timestamp'].map(h => (
                <th key={h} style={{ textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', padding: '4px 8px 6px' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {job.steps.map((step: JobStep, idx: number) => (
              <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ padding: '6px 8px', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text)' }}>
                  {step.step}
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <span style={{ color: STEP_COLORS[step.status] ?? '#94a3b8', fontSize: 12, fontWeight: 600 }}>
                    {step.status}
                  </span>
                </td>
                <td style={{ padding: '6px 8px', fontFamily: 'var(--font-mono)', fontSize: 11, color: '#94a3b8', maxWidth: 480, wordBreak: 'break-all' }}>
                  {step.error ?? (step.result ? JSON.stringify(step.result) : '—')}
                </td>
                <td style={{ padding: '6px 8px', fontSize: 11, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                  {step.at ? new Date(step.at).toLocaleTimeString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export function Events({ tenantId }: Props) {
  const { data: jobs, isLoading, error, dataUpdatedAt } = useTenantJobs(tenantId)
  const [filter, setFilter] = useState<StatusFilter>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  if (!tenantId) {
    return <div className="empty-state">Seleziona un tenant per vedere gli eventi.</div>
  }

  const displayed = (jobs ?? []).filter(j => filter === 'all' || j.status === filter)
  const hasRunning = (jobs ?? []).some(j => j.status === 'pending' || j.status === 'running')

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Events</h1>
          <p className="page-subtitle">
            Storico job di provisioning — bootstrap, playbook, riprovisioning
            {hasRunning && (
              <span style={{ marginLeft: 10, fontSize: 12, color: '#60a5fa' }}>
                ⟳ aggiornamento automatico…
              </span>
            )}
          </p>
        </div>
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
          {dataUpdatedAt ? `Aggiornato ${new Date(dataUpdatedAt).toLocaleTimeString()}` : ''}
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
        {(['all', 'failed', 'running', 'pending', 'success'] as StatusFilter[]).map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            style={{
              padding: '4px 12px',
              fontSize: 12,
              borderRadius: 'var(--radius-sm)',
              border: `1px solid ${filter === s ? 'var(--color-primary)' : 'var(--color-border)'}`,
              background: filter === s ? 'rgba(99,102,241,0.15)' : 'transparent',
              color: filter === s ? 'var(--color-primary)' : 'var(--color-text-muted)',
              cursor: 'pointer',
            }}
          >
            {s === 'all' ? 'Tutti' : s}
            {s !== 'all' && (
              <span style={{ marginLeft: 5, opacity: 0.7 }}>
                {(jobs ?? []).filter(j => j.status === s).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {isLoading && <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Caricamento…</div>}
      {error && (
        <div style={{ color: 'var(--color-danger)', fontSize: 13, background: 'var(--color-danger-dim)', border: '1px solid rgba(217,80,106,0.2)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: 16 }}>
          Errore nel caricamento degli eventi.{' '}
          <code style={{ fontSize: 11 }}>{(error as Error).message}</code>
        </div>
      )}

      {!isLoading && displayed.length === 0 && (
        <div className="empty-state">Nessun evento{filter !== 'all' ? ` con stato "${filter}"` : ''}.</div>
      )}

      {displayed.length > 0 && (
        <div style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius)',
          overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)', background: 'rgba(255,255,255,0.02)' }}>
                {['Target', 'Tipo', 'Status', 'Inizio', 'Durata', ''].map(h => (
                  <th key={h} style={{
                    textAlign: 'left', fontSize: 10, fontWeight: 600,
                    color: 'var(--color-text-muted)', textTransform: 'uppercase',
                    padding: '10px 16px',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.map(job => {
                const isExpanded = expandedId === job.id
                const duration = job.started_at && job.completed_at
                  ? `${Math.round((new Date(job.completed_at).getTime() - new Date(job.started_at).getTime()) / 1000)}s`
                  : job.started_at && !job.completed_at ? 'in corso' : '—'

                return (
                  <>
                    <tr
                      key={job.id}
                      style={{
                        borderBottom: isExpanded ? 'none' : '1px solid var(--color-border)',
                        cursor: 'pointer',
                        background: isExpanded ? 'rgba(255,255,255,0.03)' : undefined,
                      }}
                      onClick={() => setExpandedId(isExpanded ? null : job.id)}
                    >
                      <td style={{ padding: '10px 16px', fontWeight: 500 }}>
                        {job.device_name
                          ? job.device_name
                          : job.fleet_name
                            ? <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>fleet: <strong style={{ color: 'var(--color-text)' }}>{job.fleet_name}</strong></span>
                            : '—'
                        }
                      </td>
                      <td style={{ padding: '10px 16px', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{job.type}</td>
                      <td style={{ padding: '10px 16px' }}><StatusBadge status={job.status} /></td>
                      <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                        {job.started_at ? new Date(job.started_at).toLocaleString() : '—'}
                      </td>
                      <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--color-text-muted)' }}>{duration}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                          {isExpanded ? '▲ nascondi' : '▼ dettagli'}
                        </span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${job.id}-detail`} style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <td colSpan={6} style={{ padding: '0 16px 14px' }}>
                          <StepsPanel job={job} />
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
